import { COUNTRIES, LOCALE_NAMES } from "@repo/shared";
import type { ClientViewFilter } from "@repo/shared/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { buildFilterRegistry } from "@/components/clients/filters";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { DocButton } from "@/components/ui/doc-button";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useClientCostCategories } from "@/lib/hooks/use-client-cost-categories";
import {
  useClients,
  useClientTabs,
  useClientViews,
  useCustomFields,
  useEditFields,
  useSupportedLanguages,
  useTenantUsers,
} from "@/lib/hooks/use-clients";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { useTeams } from "@/lib/hooks/use-teams";
import { useUserLanguage } from "@/lib/i18n";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import type { ClientWithCustomValues } from "@/lib/types/client";
import { ClientDialog } from "./client-dialog";
import { T } from "./client-wrapper.i18n";
import { ClientsTable } from "./clients-table";
import { FilterBar, useClientListUrlState } from "./filter-bar";

const PAGE_SIZE = 20;
const DEFAULT_SORT_BY = "standard-name";

export function ClientWrapper() {
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const { customFields } = useCustomFields();
  const { editFields, isLoading: editFieldsLoading } = useEditFields();
  const { views } = useClientViews();
  const { tenantUsers, isLoading: tenantUsersLoading } = useTenantUsers();
  const { clientTabs } = useClientTabs();
  const { clientCostCategories, isLoading: costCategoriesLoading } = useClientCostCategories();
  const { teams, isLoading: teamsLoading } = useTeams();
  const { supportedLanguages } = useSupportedLanguages();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientWithCustomValues | null>(null);

  // QL-349 — filters, sort, view and page all live in the URL (a deliberate
  // deviation from this codebase's usual plain-React-state filter bar — see
  // useClientListUrlState's own doc comment for why).
  const urlState = useClientListUrlState(DEFAULT_SORT_BY);
  const [searchQuery, setSearchQuery] = useState(urlState.search);
  const debouncedSearch = useDebouncedValue(searchQuery);
  // The search text this page last wrote to the URL; a different URL value is
  // an outside change (nav link, back/forward) and replaces the input's text.
  const lastPushedSearch = useRef(urlState.search);
  useEffect(() => {
    if (urlState.search === lastPushedSearch.current) return;
    lastPushedSearch.current = urlState.search;
    setSearchQuery(urlState.search);
  }, [urlState.search]);
  useEffect(() => {
    if (debouncedSearch !== urlState.search) {
      lastPushedSearch.current = debouncedSearch;
      urlState.setSearch(debouncedSearch);
    }
    // Only the settled value should ever reach the URL — re-running this
    // when urlState.search itself changes (e.g. browser back) would fight
    // the user's still-in-progress typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Default to the tenant's default view, or "All Clients" — only once views
  // have loaded; until then the query below simply omits viewId.
  const defaultView = views.find((v) => v.isDefault);
  const selectedViewId = urlState.viewId ?? defaultView?.id ?? "standard-all-clients";

  // QL-349 — the browser's IANA zone, for any datePeriod ad-hoc filter's
  // calendar boundary. Captured once; a session doesn't change timezone.
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const registry = useMemo(
    () =>
      buildFilterRegistry({
        userLanguage,
        localize,
        customFields: editFields,
        tenantUsers,
        supportedLanguages,
        costCategories: clientCostCategories.filter((c) => c.isActive),
        teams,
        countries: COUNTRIES,
        localeNames: LOCALE_NAMES,
      }),
    [
      userLanguage,
      localize,
      editFields,
      tenantUsers,
      supportedLanguages,
      clientCostCategories,
      teams,
    ],
  );

  // QL-136 — search, the view's filters, sorting and paging all happen on the
  // server; this page only reports the advisor's choices and shows the page
  // that comes back.
  const { clients, pagination, isLoading, isFetching, error } = useClients({
    page: urlState.page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch,
    viewId: selectedViewId,
    sortBy: urlState.sortBy,
    sortDir: urlState.sortDir,
    filters: urlState.filters,
    timezone,
  });

  // A page can empty out after a delete on the last page; step back to the
  // last page that still exists so the pager stays reachable.
  // Only against a settled response: before the first one arrives (or when it
  // fails) `totalPages` is a placeholder 1, which would wipe a deep-linked page.
  useEffect(() => {
    if (isLoading || isFetching || error) return;
    if (urlState.page > pagination.totalPages) urlState.setPage(pagination.totalPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlState.page, pagination.totalPages, isLoading, isFetching, error]);

  // QL-115 — clicking the active column flips direction; a new column starts
  // ascending for text and descending for numbers (biggest first), as the
  // in-browser sort used to.
  const toggleSort = (fieldId: string) => {
    if (fieldId === urlState.sortBy) {
      urlState.setSort(fieldId, urlState.sortDir === "asc" ? "desc" : "asc");
      return;
    }
    const field = customFields.find((f) => f.id === fieldId);
    urlState.setSort(fieldId, field?.type === "number" ? "desc" : "asc");
  };

  const selectedView = views.find((v) => v.id === selectedViewId);

  // QL-102 — "No clients yet. Create your first client" is wrong once a view,
  // an ad-hoc filter, or a search has filtered them all away: the clients
  // exist, none of them match.
  const viewFilters = selectedView?.filters ?? [];
  const hasAnyFilter = viewFilters.length > 0 || urlState.filters.length > 0;
  const emptyMessage = debouncedSearch.trim()
    ? t.noSearchResults
    : viewFilters.some((f: ClientViewFilter) => f.type === "favourite")
      ? t.noFavourites
      : hasAnyFilter
        ? t.noMatchingClients
        : undefined;

  const handleCreate = () => {
    setEditingClient(null);
    setDialogOpen(true);
  };

  const handleEdit = (client: ClientWithCustomValues) => {
    setEditingClient(client);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingClient(null);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t.pageTitle}
        description={t.pageDescription}
        actions={
          <DocButton docSlug="clients" variant="outline" size="sm" className="gap-2">
            {t.documentation}
          </DocButton>
        }
      />

      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex flex-wrap items-end gap-4 flex-1">
          {views.length > 0 && (
            <Select value={selectedViewId} onValueChange={urlState.setViewId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t.selectView} />
              </SelectTrigger>
              <SelectContent>
                {views.map((view) => (
                  <SelectItem key={view.id} value={view.id}>
                    {localize(view.nameI18n)}
                    {view.isDefault && ` (${t.default})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <SearchInput
            className="w-80"
            placeholder={t.searchPlaceholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          {/* The bar seeds its rows from the URL against the registry, so it mounts only once the registry's async inputs have loaded. */}
          {!(editFieldsLoading || tenantUsersLoading || costCategoriesLoading || teamsLoading) && (
            <FilterBar
              filters={urlState.filters}
              onFiltersChange={urlState.setFilters}
              registry={registry}
              userLanguage={userLanguage}
            />
          )}
        </div>
        <Button onClick={handleCreate}>{t.addClient}</Button>
      </div>

      <ClientsTable
        clients={clients}
        customFields={customFields}
        onEdit={handleEdit}
        emptyMessage={emptyMessage}
        sortBy={urlState.sortBy}
        sortDir={urlState.sortDir}
        onSort={toggleSort}
        page={pagination.page}
        pageSize={pagination.pageSize}
        totalCount={pagination.totalCount}
        totalPages={pagination.totalPages}
        onPageChange={urlState.setPage}
        isLoading={isLoading}
        isFetching={isFetching}
      />

      <ClientDialog
        open={dialogOpen}
        client={editingClient}
        customFields={editFields}
        clientTabs={clientTabs}
        onClose={handleDialogClose}
      />
    </div>
  );
}
